import { defineStore } from 'pinia';
import { object, ref, string } from 'yup';
import AuthAPI from '~/api/AuthAPI';
import type { FormSetErrors, VerifyEmailRequestCode, VerifyEmailRequestSigned } from '~/types';
import { setAuthUser, updateAuthUser } from '#imports';

export const useAuthProcessStore = defineStore('auth-process', {
  state: () => ({
    loading: false,
    showPassword: false,
    data: {
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
    },
  }),
  getters: {
    isLoading(): boolean {
      return this.loading;
    },
    passwordInputType(): string {
      return this.showPassword
        ? 'text'
        : 'password';
    },
  },
  actions: {
    generateRandomPassword() {
      const random = Math.random().toString(36).substring(2, 17);
      this.data.password = random;
      this.data.password_confirmation = random;
      this.showPassword = true;
    },

    async login(setErrors: FormSetErrors): Promise<void> {
      this.loading = true;
      try {
        const user = await AuthAPI.login({
          email: this.data.email,
          password: this.data.password,
        });
        setAuthUser(user);
        const route = useRoute();
        const redirect = route.query.redirect as string | undefined || '/projects';
        logger().debug(`Redirecting after login to "${redirect}"`);
        navigateTo(redirect);
      } catch (e: any) {
        if (isBackendValidationError(e)) {
          setErrors(e.response.data.errors);
        } else {
          logger().error(e);
        }
      } finally {
        this.loading = false;
      }
    },

    loginSchema() {
      return object({
        email: string().required().email(),
        password: string().required(),
      }).required();
    },

    async register(setErrors: FormSetErrors): Promise<void> {
      this.loading = true;
      try {
        const user = await AuthAPI.register({
          name: this.data.name,
          email: this.data.email,
          password: this.data.password,
          password_confirmation: this.data.password_confirmation,
        });
        setAuthUser(user);
        const route = useRoute();
        const redirect = route.query.redirect as string | undefined || '/projects/create';
        navigateTo(redirect);
      } catch (e: any) {
        if (isBackendValidationError(e)) {
          setErrors(e.response.data.errors);
        } else {
          logger().error(e);
        }
      } finally {
        this.loading = false;
      }
    },

    registerSchema() {
      return object({
        name: string().required(),
        email: string().required().email(),
        password: string().required(),
        password_confirmation: string().required().oneOf([ref('password')]),
      }).required();
    },

    async passwordRecovery(setErrors: FormSetErrors): Promise<string | unknown> {
      this.loading = true;
      try {
        const { message } = await AuthAPI.passwordRecovery({
          email: this.data.email,
        });
        return message;
      } catch (e: any) {
        if (isBackendValidationError(e)) {
          setErrors(e.response.data.errors);
        } else {
          logger().error(e);
        }
      } finally {
        this.loading = false;
      }
    },

    passwordRecoverySchema() {
      return object({
        email: string().required().email(),
      }).required();
    },

    async passwordReset(token: string, email: string, setErrors: FormSetErrors): Promise<string | unknown> {
      this.loading = true;
      try {
        this.data.email = email;
        const { message } = await AuthAPI.passwordReset({
          token,
          email,
          password: this.data.password,
          password_confirmation: this.data.password_confirmation,
        });
        return message;
      } catch (e: any) {
        if (isBackendValidationError(e)) {
          setErrors(e.response.data.errors);
        } else {
          logger().error(e);
        }
      } finally {
        this.loading = false;
      }
    },

    passwordResetSchema() {
      return object({
        password: string().required(),
        password_confirmation: string()
          .required()
          .oneOf([ref('password')]),
      }).required();
    },

    togglePasswordVisible() {
      this.showPassword = !this.showPassword;
    },

    async verifyEmailSigned(requestData: VerifyEmailRequestSigned) {
      const { emailVerifiedAt, updatedAt } = await AuthAPI.verifyEmailSigned(requestData);
      updateAuthUser({ emailVerifiedAt, updatedAt });
    },

    async verifyEmailCode(requestData: VerifyEmailRequestCode) {
      const { emailVerifiedAt, updatedAt } = await AuthAPI.verifyEmailCode(requestData);
      updateAuthUser({ emailVerifiedAt, updatedAt });
    },

    async sendVerificationCodeToEmail(): Promise<string> {
      const { message } = await AuthAPI.sendVerificationCodeToEmail();
      return message;
    },
  },
});
